import type { Card, ForkedAnswer, LinearAnswer } from '@dmp/qqms/types';
import { truthy } from '@dmp/shared/helpers';
import { omit } from 'remeda';
import { coreQuestionResourceToCoreQuestion } from '../../transformers/core-question-conversions';
import {
    isDynamicCoreQuestionItem,
    isForkedQuestionItem,
    isLinearQuestionItem,
    isPollResultCardItem,
    isThankYouCardItem,
    isTitleCardItem,
} from '../state-type-guards';
import type { CardState } from '../state-types';
import { useStore } from '../use-store';

/**
 * [Expensive calculation] Build Card Config from Card State
 * `build-card-config` depends on answers and core questions, it should only perform calculations when the `card` prop changes.
 * 
 * ```tsx
 * // get card from store, this is not shallowly memoized.
 * const card = useStore(useShallow((s) => s.actions.getCard(cardId)));
 * // then build the card config.
 * const cardConfig = useMemo(() => buildCardConfig(card), [card]);
 * ```
```
 */
export const buildCardConfig = (
    cardState: CardState | null | undefined
): Card | null => {
    if (!cardState) {
        return null;
    }

    const cardId = cardState.id;
    const getCardAnswers = useStore.getState().actions.getCardAnswers;

    // Linear Question
    if (isLinearQuestionItem(cardState)) {
        const cardAnswers = getCardAnswers(cardId);
        return {
            ...omit(cardState, ['answerIds']),
            answers: cardAnswers as LinearAnswer[],
        };
    }

    // Forked Question
    if (isForkedQuestionItem(cardState)) {
        const cardAnswers = getCardAnswers(cardId);
        return {
            ...omit(cardState, ['answerIds']),
            answers: cardAnswers as ForkedAnswer[],
        };
    }

    // Dynamic Core Question
    if (isDynamicCoreQuestionItem(cardState)) {
        const coreQuestionList = useStore
            .getState()
            .actions.getCoreQuestionList();

        const questions = cardState.questionIds
            .map((q) =>
                coreQuestionList.find((c) => c.attributes.shortId === q)
            )
            .filter(truthy)
            .map(coreQuestionResourceToCoreQuestion);

        return { ...cardState, questions };
    }

    // Title Card
    if (isTitleCardItem(cardState)) {
        return cardState;
    }

    // ThankYou card
    if (isThankYouCardItem(cardState)) {
        return cardState;
    }

    // Poll Result card
    if (isPollResultCardItem(cardState)) {
        return cardState;
    }

    throw new Error(`Cannot convert Card Item State to Card Config.`);
};
